import { CloseCircleFilled, DownOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import {
  Button,
  DatePicker as DDatePicker,
  type DatePickerProps as DDatePickerProps,
  Space,
  theme,
  Typography,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { type ReactNode, useEffect, useRef, useState } from 'react';

// const dateFormat = 'YYYY-MM-DD';
const displayFormat = 'MMM D, YY';
const DEFAULT_DATE = dayjs().startOf('d');

const StyledButton = styled(Button)`
  &:focus-visible {
    outline-width: 0 !important;
    outline-offset: 0 !important;
    box-shadow: 0 0 0 2px rgb(255 5 145 / 10%);
  }
`;

interface DatePresetType {
  label: string;
  value: Dayjs;
  key: string;
}

type DatePickerProps = {
  autoFocus?: boolean;
  defaultOpen?: boolean;
  minDate?: Dayjs | undefined;
  value: Dayjs | undefined;
  title: string;
  onChange: (date: Dayjs) => void;
} & DDatePickerProps;

const DatePicker = (props: DatePickerProps): ReactNode => {
  const defaultButtonLabel = 'Today';
  const {
    autoFocus = false,
    defaultOpen = false,
    value,
    onChange,
    title,
    minDate,
    ...datePickerProps
  } = props;
  const [isFocused, setFocus] = useState(autoFocus);
  const [isPickerOpened, setPickerOpen] = useState(defaultOpen);
  const [isButtonHovered, setButtonHovered] = useState(false);
  const [buttonLabel, setButtonLabel] = useState(defaultButtonLabel);
  const isSelected = useRef(value != null);
  const { token } = theme.useToken();

  useEffect(() => {
    isSelected.current = value != null;
    setButtonLabel(value == null ? defaultButtonLabel : dayjs(value).format(displayFormat));
  }, [value]);

  const onValueChange = (date: Dayjs, clickedItem: DatePresetType | undefined): void => {
    setButtonLabel(clickedItem ? clickedItem.label : dayjs(value).format(displayFormat));
    isSelected.current = !!date;
    onChange(date);
    setFocus(false);
  };

  const openPicker = (): void => {
    setPickerOpen(true);
  };

  return (
    <div style={{ position: 'relative' }}>
      <StyledButton
        onClick={() => {
          openPicker();
        }}
        onFocus={() => {
          setFocus(true);
        }}
        onBlur={() => {
          setFocus(false);
        }}
        onMouseEnter={() => {
          setButtonHovered(true);
        }}
        onMouseLeave={() => {
          setButtonHovered(false);
        }}
        style={{
          paddingLeft: token.paddingXXS,
          paddingRight: token.paddingXXS,
          borderColor: isButtonHovered
            ? token.colorPrimary
            : isFocused
            ? token.colorPrimaryHover
            : isSelected.current
            ? token.colorPrimaryBg
            : undefined,
          // background: isSelected.current ? token.colorPrimaryBg : undefined,
        }}
      >
        <Space
          style={{
            marginRight: 3,
            paddingLeft: token.paddingXS,
            paddingRight: token.paddingXS,
          }}
        >
          <Typography.Text type="secondary">{title}</Typography.Text>
          <Typography.Text strong style={isSelected.current ? { color: token.colorPrimary } : {}}>
            {buttonLabel}
          </Typography.Text>
          <Typography.Text style={{ fontSize: token.fontSizeSM, color: token.colorTextDisabled }}>
            {isButtonHovered && value != null ? (
              <CloseCircleFilled
                onClick={(e) => {
                  setFocus(false);
                  isSelected.current = false;
                  onChange(dayjs().startOf('d'));
                  setButtonHovered(false);
                  e.preventDefault();
                  e.stopPropagation();
                }}
              />
            ) : (
              <DownOutlined />
            )}
          </Typography.Text>
        </Space>
      </StyledButton>

      <DDatePicker
        open={isPickerOpened}
        suffixIcon={null}
        variant="borderless"
        minDate={minDate}
        style={{
          overflow: 'hidden',
          zIndex: -1,
          position: 'absolute',
          paddingLeft: token.paddingXXS,
          paddingRight: token.paddingXXS,
          left: token.paddingXXS + 2,
          width: `calc(100% - ${token.paddingXS + 2}px)`,
        }}
        onChange={(date) => {
          onValueChange(date ?? DEFAULT_DATE, undefined);
        }}
        onOpenChange={(nextOpen) => {
          setFocus(nextOpen);
          setPickerOpen(nextOpen);
        }}
        {...datePickerProps}
      />
    </div>
  );
};
DatePicker.defaultProps = {
  autoFocus: false,
  defaultOpen: false,
  minDate: undefined,
};
export default DatePicker;
